/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, CircularProgress, Step, StepContent, StepLabel, Stepper, Typography } from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import Modal from "../../Components/Modal";
import { useEffect, useState } from "react";
import { deleteData } from "../../Services/deleteData";
import { UrlEnum } from "../../Utils/Utils";
import { postData } from "../../Services/postData";
import { toast } from "react-toastify";
import { getData } from "../../Services/getData";
import moment from "moment";

export type FanCourierOverrides = {
  generateAWBUrl?: string;
  buildDeleteAWBUrl?: (awb: string) => string;
  createFanOrderUrl?: string | null;
  buildDeleteOrderUrl?: (awb: string) => string;
};

type FanCourierComponentProps = {
  fanCourierInfo: any;
  fanCourierInfoReturn?: any;
  order: any;
  handleChangeOrder: (order: any) => void;
  saveOrder: (order: any) => Promise<any>;
  overrides?: FanCourierOverrides;
};

export default function FanCourierComponent(props: FanCourierComponentProps) {
  const { fanCourierInfo, fanCourierInfoReturn, order, handleChangeOrder, saveOrder, overrides } = props;
  const [openDeleteSendAWB, setOpenDeleteSendAWB] = useState(false);
  const [openDeleteReturnAWB, setOpenDeleteReturnAWB] = useState(false);
  const [openDeleteOrder, setOpenDeleteOrder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [trackingInfoSend, setTrackingInfoSend] = useState<any>(null);
  const [trackingInfoReturn, setTrackingInfoReturn] = useState<any>(null);

  useEffect(() => {
    if (!fanCourierInfo?.awb) return;
    setTimeout(() => {
      getData(`${UrlEnum.trackAwb}/${fanCourierInfo.awb}`).then((res) => {
        if (res?.data?.length) setTrackingInfoSend(res.data[0]);
      });
    }, 2000);
  }, [fanCourierInfo?.awb]);

  useEffect(() => {
    if (!fanCourierInfoReturn?.awb) return;
    setTimeout(() => {
      getData(`${UrlEnum.trackAwb}/${fanCourierInfoReturn.awb}`).then((res) => {
        if (res?.data?.length) setTrackingInfoReturn(res.data[0]);
      });
    }, 2000);
  }, [fanCourierInfoReturn?.awb]);

  async function generateAWB(isReturn: boolean) {
    setLoading(true);
    const baseUrl = overrides?.generateAWBUrl ?? `${UrlEnum.orders}/generateAWB/${order?._id}`;
    const url = isReturn ? `${baseUrl}?returnAWB=true` : baseUrl;
    const res = await postData(url);
    if (res?.data) {
      handleChangeOrder(res.data);
    } else {
      toast.error(res?.data?.message || Vocabulary.error);
    }
    setLoading(false);
  }

  async function createOrder() {
    if (overrides?.createFanOrderUrl === null) return;
    const momentDiff = moment().diff(moment(order?.shipping?.pickupDate, "YYYY-MM-DDTHH:mm:ss"), "hours");
    if (momentDiff > 0) {
      toast.error(Vocabulary.fanOrderPickupError);
      setLoading(false);
      return;
    }
    setLoading(true);
    saveOrder(false).then(async () => {
      const url = overrides?.createFanOrderUrl ?? `${UrlEnum.orders}/createFanOrder/${order?._id}`;
      const res = await postData(url);
      if (res?.data) {
        handleChangeOrder(res.data);
      } else {
        toast.error(res?.data?.message || Vocabulary.error);
      }
      setLoading(false);
    });
  }

  async function handleDeleteAWB(isReturn: boolean) {
    if (isReturn) setOpenDeleteReturnAWB(false);
    else setOpenDeleteSendAWB(false);
    setLoading(true);
    const awbValue = isReturn ? fanCourierInfoReturn?.awb : fanCourierInfo?.awb;
    let url: string;
    if (overrides?.buildDeleteAWBUrl) {
      url = overrides.buildDeleteAWBUrl(awbValue);
    } else {
      url = `${UrlEnum.orders}/deleteAWB/${order?._id}${isReturn ? "?returnAWB=true" : ""}`;
    }
    const res = await deleteData(url);
    if (res?.data) {
      handleChangeOrder(res.data);
    } else {
      toast.error(res?.data?.message || Vocabulary.error);
    }
    setLoading(false);
  }

  async function handleDeleteOrder() {
    setOpenDeleteOrder(false);
    setLoading(true);
    const url = overrides?.buildDeleteOrderUrl
      ? overrides.buildDeleteOrderUrl(fanCourierInfo?.awb)
      : `${UrlEnum.orders}/deleteOrder/${order?._id}`;
    const res = await deleteData(url);
    if (res?.data) {
      handleChangeOrder(res.data);
    } else {
      toast.error(res?.data?.message || Vocabulary.error);
    }
    setLoading(false);
  }

  function TrackingDisplay({ trackingInfo }: { trackingInfo: any }) {
    if (!trackingInfo?.events) return null;
    return (
      <Stepper orientation="vertical">
        {trackingInfo.events.map((step: { label: string; location: string; date: string; name: string }) => (
          <Step key={step.label} active>
            <StepLabel>
              <Typography><strong>{step.name}</strong></Typography>
            </StepLabel>
            <StepContent>
              <Typography>{step.location}</Typography>
              <Typography>{step.date}</Typography>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    );
  }

  return (
    <div style={{ marginTop: "20px" }}>
      {/* Send AWB section */}
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{Vocabulary.awbSend}</Typography>
      {fanCourierInfo?.awb ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
            <Typography>
              <span>{Vocabulary.awb}: &nbsp;</span>
              <span style={{ fontWeight: 600 }}>{fanCourierInfo.awb}</span>
            </Typography>
            {!fanCourierInfo.orderNumber && (
              <Button variant="outlined" color="error" size="small" onClick={() => setOpenDeleteSendAWB(true)}>
                {Vocabulary.deleteAWB}
              </Button>
            )}
          </div>
          {fanCourierInfo.awb && !fanCourierInfo.orderNumber && overrides?.createFanOrderUrl !== null && (
            <Button variant="outlined" onClick={createOrder}>
              {Vocabulary.placeOrderToCourier}
            </Button>
          )}
          {fanCourierInfo.orderNumber && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                <Typography>
                  <span>{Vocabulary.fanCourierOrderNumber}: &nbsp;</span>
                  <span style={{ fontWeight: 600 }}>{fanCourierInfo.orderNumber}</span>
                </Typography>
                <Button variant="outlined" color="error" size="small" onClick={() => setOpenDeleteOrder(true)}>
                  {Vocabulary.deleteFanCourierOrder}
                </Button>
              </div>
              <Typography variant="h5" style={{ marginTop: 20 }}>{Vocabulary.trackingInfo}</Typography>
              <TrackingDisplay trackingInfo={trackingInfoSend} />
            </>
          )}
        </>
      ) : (
        <Button variant="outlined" onClick={() => generateAWB(false)} style={{ marginBottom: 8 }}>
          {Vocabulary.generateAWB}
        </Button>
      )}

      {/* Return AWB section */}
      <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 16 }}>{Vocabulary.awbReturn}</Typography>
      {fanCourierInfoReturn?.awb ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
            <Typography>
              <span>{Vocabulary.awb}: &nbsp;</span>
              <span style={{ fontWeight: 600 }}>{fanCourierInfoReturn.awb}</span>
            </Typography>
            <Button variant="outlined" color="error" size="small" onClick={() => setOpenDeleteReturnAWB(true)}>
              {Vocabulary.deleteAWB}
            </Button>
          </div>
          <Typography variant="h5" style={{ marginTop: 20 }}>{Vocabulary.trackingInfo}</Typography>
          <TrackingDisplay trackingInfo={trackingInfoReturn} />
        </>
      ) : (
        <Button variant="outlined" onClick={() => generateAWB(true)} style={{ marginBottom: 8 }}>
          {Vocabulary.generateReturnAWB}
        </Button>
      )}

      {/* Delete modals */}
      {openDeleteSendAWB && (
        <Modal open={openDeleteSendAWB} onClose={() => setOpenDeleteSendAWB(false)} title={Vocabulary.deleteAWB}>
          <Typography variant="h5" style={{ textAlign: "center", marginBottom: 20 }}>
            {Vocabulary.deleteAWBMessage}
          </Typography>
          <Button variant="contained" color="error" onClick={() => setOpenDeleteSendAWB(false)} style={{ float: "left" }}>
            {Vocabulary.no}
          </Button>
          <Button variant="contained" onClick={() => handleDeleteAWB(false)} style={{ float: "right" }}>
            {Vocabulary.yes}
          </Button>
        </Modal>
      )}
      {openDeleteReturnAWB && (
        <Modal open={openDeleteReturnAWB} onClose={() => setOpenDeleteReturnAWB(false)} title={Vocabulary.deleteAWB}>
          <Typography variant="h5" style={{ textAlign: "center", marginBottom: 20 }}>
            {Vocabulary.deleteAWBMessage}
          </Typography>
          <Button variant="contained" color="error" onClick={() => setOpenDeleteReturnAWB(false)} style={{ float: "left" }}>
            {Vocabulary.no}
          </Button>
          <Button variant="contained" onClick={() => handleDeleteAWB(true)} style={{ float: "right" }}>
            {Vocabulary.yes}
          </Button>
        </Modal>
      )}
      {openDeleteOrder && (
        <Modal open={openDeleteOrder} onClose={() => setOpenDeleteOrder(false)} title={Vocabulary.deleteFanCourierOrder}>
          <Typography variant="h5" style={{ textAlign: "center", marginBottom: 20 }}>
            {Vocabulary.deleteFanCourierOrderMessage}
          </Typography>
          <Button variant="contained" color="error" onClick={() => setOpenDeleteOrder(false)} style={{ float: "left" }}>
            {Vocabulary.no}
          </Button>
          <Button variant="contained" onClick={handleDeleteOrder} style={{ float: "right" }}>
            {Vocabulary.yes}
          </Button>
        </Modal>
      )}
      {loading && (
        <div className="loading">
          <CircularProgress color="primary" />
        </div>
      )}
    </div>
  );
}
